import { Lock, Network } from "lucide-react";
import { Link } from "react-router";
import { companyPath } from "~/lib/company-profile";
import type {
	RelatedPartiesResponse,
	RelatedPartyEntity,
	RelatedPartyReportItem,
} from "~/types/corporate";

const RELATION_TYPE_LABELS: Record<string, string> = {
	ovladani: "Vztah ovládání",
	koncern: "Koncern",
};

/** Czech 3-form pluralization: 1 zpráva / 2–4 zprávy / 5+ zpráv. */
function pluralizeReports(count: number): string {
	if (count === 1) return "zpráva o vztazích";
	if (count >= 2 && count <= 4) return "zprávy o vztazích";
	return "zpráv o vztazích";
}

/** Colored chip for the detriment (újma) statement of the report. */
function DetrimentChip({ report }: { report: RelatedPartyReportItem }) {
	if (report.detrimentOccurred === false) {
		return <span className="badge badge-soft badge-success badge-sm shrink-0">Újma nevznikla</span>;
	}
	if (report.detrimentOccurred === true) {
		return (
			<span className="badge badge-soft badge-error badge-sm shrink-0">
				Újma vznikla{report.detrimentSettled ? " (vyrovnána)" : ""}
			</span>
		);
	}
	return <span className="badge badge-soft badge-ghost badge-sm shrink-0">Neuvedeno</span>;
}

function EntityRow({ entity }: { entity: RelatedPartyEntity }) {
	return (
		<li className="flex items-baseline justify-between gap-3 rounded-md border border-gray-100 px-3 py-2 text-sm">
			<span className="min-w-0 truncate">
				{entity.ico ? (
					<Link
						to={companyPath(entity.ico, entity.name)}
						prefetch="intent"
						className="text-primary hover:underline"
					>
						{entity.name}
					</Link>
				) : (
					entity.name
				)}
				{entity.isNaturalPerson && (
					<span className="ml-2 text-xs text-muted-foreground">fyzická osoba</span>
				)}
			</span>
			{entity.sharePercent != null && (
				<span className="shrink-0 font-mono text-xs font-medium tabular-nums">
					{entity.sharePercent.toLocaleString("cs-CZ", { maximumFractionDigits: 2 })} %
				</span>
			)}
		</li>
	);
}

function UpsellBanner({ anon }: { anon: boolean }) {
	return (
		<div className="mt-4 rounded-lg border border-dashed border-primary-200 bg-primary-50 p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
			<div className="flex items-start gap-3">
				<Lock className="h-4 w-4 text-primary-600 mt-0.5 shrink-0" />
				<div className="flex flex-col">
					<p className="text-sm font-medium text-foreground">
						{anon
							? "Detaily skupiny po registraci zdarma"
							: "Kompletní zprávy o vztazích v plánu Premium"}
					</p>
					<p className="text-xs text-muted-foreground mt-0.5">
						{anon
							? "Po registraci uvidíte ovládající osoby z poslední zprávy. Premium odemkne kompletní strukturu skupiny, smlouvy i prohlášení o újmě."
							: "Premium plán odemkne všechny zprávy, propojené osoby, smlouvy ve skupině a poznámky k újmě."}
					</p>
				</div>
			</div>
			<Link
				to={anon ? "/login?mode=register" : "/cenik"}
				className="btn btn-sm btn-primary shrink-0"
			>
				{anon ? "Zaregistrujte se zdarma" : "Upgradovat"}
			</Link>
		</div>
	);
}

/**
 * "Podnikatelské seskupení" — group structure extracted from the related-party
 * report (zpráva o vztazích, § 82 ZOK). Renders nothing when the company has
 * no extracted reports.
 */
export function RelatedPartiesSection({ response }: { response: RelatedPartiesResponse | null }) {
	if (!response || response.total === 0) return null;
	const { reports, total, locked, viewerTier } = response;
	const latest = reports[0] ?? null;

	const relationType = latest?.relationType
		? (RELATION_TYPE_LABELS[latest.relationType] ?? latest.relationType)
		: null;
	const controlling = latest?.controllingEntities ?? [];
	const otherRelated = latest?.otherRelatedEntities ?? [];

	return (
		<div className="card-surface rounded-xl p-6">
			<div className="flex items-center gap-2 mb-4">
				<div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary-50">
					<Network className="h-4 w-4 text-primary-600" />
				</div>
				<h2 className="text-lg font-semibold">Podnikatelské seskupení</h2>
				<span className="text-xs text-muted-foreground ml-auto">
					{total} {pluralizeReports(total)}
				</span>
			</div>

			{latest ? (
				<div className="flex flex-col gap-4">
					<div className="flex flex-wrap items-center gap-2">
						{latest.year != null && (
							<span className="badge badge-soft badge-sm shrink-0 font-mono tabular-nums">
								Zpráva za rok {latest.year}
							</span>
						)}
						{relationType && (
							<span className="badge badge-soft badge-sm shrink-0">{relationType}</span>
						)}
						<DetrimentChip report={latest} />
					</div>

					{controlling.length > 0 && (
						<div>
							<h3 className="text-sm font-semibold text-muted-foreground mb-2 border-b border-gray-100 pb-1">
								Ovládající osoby
							</h3>
							<ul className="flex flex-col gap-2">
								{controlling.map((entity, index) => (
									<EntityRow key={`${entity.name}-${index}`} entity={entity} />
								))}
							</ul>
						</div>
					)}

					{otherRelated.length > 0 && (
						<div>
							<h3 className="text-sm font-semibold text-muted-foreground mb-2 border-b border-gray-100 pb-1">
								Další propojené osoby
							</h3>
							<ul className="flex flex-col gap-2">
								{otherRelated.map((entity, index) => (
									<EntityRow key={`${entity.name}-${index}`} entity={entity} />
								))}
							</ul>
						</div>
					)}

					{!locked && latest.detrimentNote && (
						<p className="text-xs text-muted-foreground">Újma: {latest.detrimentNote}</p>
					)}
					{!locked && latest.advantagesNote && (
						<p className="text-xs text-muted-foreground">
							Výhody a nevýhody členství ve skupině: {latest.advantagesNote}
						</p>
					)}
				</div>
			) : (
				<p className="text-sm text-muted-foreground">
					{total === 1
						? "Firma má ve Sbírce listin 1 zprávu o vztazích."
						: total <= 4
							? `Firma má ve Sbírce listin ${total} zprávy o vztazích.`
							: `Firma má ve Sbírce listin ${total} zpráv o vztazích.`}{" "}
					Ovládající osoby a strukturu skupiny zobrazíte po registraci.
				</p>
			)}

			{locked && <UpsellBanner anon={viewerTier === "anon"} />}
		</div>
	);
}
